import {
    CaretSortIcon,
    CheckIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import debounce from '../lib/debounce';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./ui/command";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from './ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./ui/popover";

const SelectBunnyPopover = ({ form, name, label, gender, onSelect, selectedBunny, allowDead = false }) => {
    const [state, setState] = useState({
        bunnyName: '',
        bunnies: selectedBunny || [],
        isPopoverOpen: false,
        isLoading: false,
        selectedBunny: selectedBunny ? selectedBunny[0]?.id : null,
    });

    useEffect(() => {
        if (selectedBunny) {
            setState(prevState => ({
                ...prevState,
                bunnies: selectedBunny,
                selectedBunny: selectedBunny[0]?.id,
            }));
        }
    }, [selectedBunny]);

    const fetchBunnies = (query) => {
        setState(prevState => ({ ...prevState, isLoading: true }));

        api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/search?name=${query}&allowDead=${allowDead}`)
            .then(response => {
                const filteredBunnies = response.data.filter(bunny => bunny.gender === gender);
                setState(prevState => ({
                    ...prevState,
                    bunnies: gender ? filteredBunnies : response.data,
                    isLoading: false,
                }));
            })
            .catch(() => {
                setState(prevState => ({ ...prevState, isLoading: false }));
            });
    };

    const debouncedFetchBunnies = useCallback(
        debounce((value) => {
            if (value.length > 2) {
                fetchBunnies(value);
            } else {
                setState(prevState => ({ ...prevState, bunnies: [] }));
            }
        }, 500),
        []
    );

    const handleSearchInputChange = (e) => {
        const value = e.target.value;

        if (state.selectedBunny) {
            form.setValue(name, null);
            setState(prevState => ({
                ...prevState,
                selectedBunny: null,
            }));
        }

        setState(prevState => ({
            ...prevState,
            bunnyName: value,
            bunnies: [],
            isPopoverOpen: true,
        }));

        debouncedFetchBunnies(value);
    };

    const handleSelectBunny = (bunnyId) => {
        form.setValue(name, bunnyId);
        onSelect(bunnyId);
        setState(prevState => ({
            ...prevState,
            selectedBunny: bunnyId,
            isPopoverOpen: false,
        }));
    };

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className='space-y-0 grid gap-2'>
                    <FormLabel>{label}</FormLabel>
                    <Popover modal={true} open={state.isPopoverOpen} onOpenChange={(open) => setState(prevState => ({ ...prevState, isPopoverOpen: open }))}>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    className={cn(
                                        "w-full justify-between",
                                        !state.selectedBunny && "text-muted-foreground"
                                    )}
                                >
                                    {state.selectedBunny
                                        ? (() => {
                                            const selectedBunny = state.bunnies.find(
                                                (bunny) => bunny.id === state.selectedBunny
                                            );

                                            return selectedBunny
                                                ? selectedBunny.full_name +
                                                (selectedBunny.registration_number
                                                    ? ` (${selectedBunny.registration_number})`
                                                    : "")
                                                : "Välj kanin";
                                        })()
                                        : "Välj kanin"}

                                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="popover-content-width-full p-0">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    placeholder="Sök kanin..."
                                    onInput={handleSearchInputChange}
                                    value={state.bunnyName}
                                    autoFocus
                                />
                                <CommandList>
                                    {state.isLoading && <div className='flex justify-center items-center w-full min-h-16'><UpdateIcon className='loader-spinner size-6' /></div>}
                                    {!state.isLoading && <CommandEmpty>Ingen kanin hittades.</CommandEmpty>}
                                    <CommandGroup>
                                        {state.bunnies.map((bunny) => (
                                            <CommandItem
                                                className="cursor-pointer"
                                                value={bunny.id}
                                                key={bunny.id}
                                                onSelect={() => handleSelectBunny(bunny.id)}
                                            >
                                                <CheckIcon
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        bunny.id === field.value
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                    )}
                                                />
                                                {bunny.full_name} {bunny.registration_number ? '(' + bunny.registration_number + ')' : null}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
};

export default SelectBunnyPopover;
